import * as _ from 'lodash';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { OrganizationSsoMethod } from '../../../../../../typings/enums';
import { window } from '../../../../../globals';
import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../../../../helpers/errors';
import useOnMount from '../../../../../hooks/useOnMount';
import useAuthenticationSettingsContext from '../hooks/useAuthenticationSettingsContext';
import type { ClaimSamlDomainRequest } from '../../../../../../typings/api';

import CheckEmailModalBody from './CheckEmailModalBody';
import { AuthLdapStepBody, AuthLdapStepButtons } from './Google/ClaimDomainModal/AuthLdapStep';
import { AuthSamlStepBody, AuthSamlStepButtons } from './Google/ClaimDomainModal/AuthSamlStep';
import { DoneStepBody } from './Google/ClaimDomainModal/DoneStep';
import { EnterDomainStepBody, EnterDomainStepButtons } from './Google/ClaimDomainModal/EnterDomainStep';
import { EnterEmailStepBody, EnterEmailStepButtons } from './Google/ClaimDomainModal/EnterEmailStep';
import useClaimLDAPDomain from './hooks/useClaimLDAPDomain';
import useClaimSAMLDomain from './hooks/useClaimSAMLDomain';
import { ClaimDomainStep } from './types';

// possibleInitialSteps lists the steps that can possibly be the first step shown to the user when the page loads.
const possibleInitialSteps = [
  ClaimDomainStep.EnterDomain,
  ClaimDomainStep.CheckEmail,
  ClaimDomainStep.Done,
  ClaimDomainStep.Error,
];

type Props = {
  method: OrganizationSsoMethod.SAML | OrganizationSsoMethod.LDAP;
  isOpen: boolean;
  onHide: () => void;
};

/**
 * This modal is shared between SAML and LDAP. It is used to claim a domain for SSO.
 *
 * For SAML, the steps are:
 * 1. Enter the domain to be claimed.
 * 2. Enter the email address of a user with an email address that matches the domain.
 * 3. Prompt user to Sign in with SSO.
 *
 * For LDAP, the steps are:
 * 1. Enter the domain to be claimed.
 * 2. Enter the email address of a user with an email address that matches the domain.
 * 3. Enter the LDAP username and password of a user with an email address that matches the domain.
 * 4. Prompt user to check their email. Allow them to click on "Resend email" if they don't receive it.
 */
const MultiStepsClaimDomainModal: VoidFunctionComponent<Props> = ({ method, onHide, isOpen }) => {
  const [isSubmittingConfiguration, setIsSubmittingConfiguration] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState<string[] | null>(null);
  const ssoConfigurationStatus = {
    errors: submissionErrors,
    loading: isSubmittingConfiguration,
    isLoading: isSubmittingConfiguration,
  };
  const { location, settings } = useAuthenticationSettingsContext();
  const sso = _.pick(settings.saml, 'entryPoint', 'cert', 'audience');
  const {
    ssoDomainClaimed: claimedDomain,
    domainVerificationError,
  } = location?.query || {};
  const organizationId = window.user?.organizationId as number;
  const { claimSAMLDomain } = useClaimSAMLDomain(organizationId);
  const { claimLDAPDomain } = useClaimLDAPDomain(organizationId);

  const [step, setStep] = useState<ClaimDomainStep>(() => {
    // The Done step will be the initial if you were just redirected back to the settings page after following the link
    // given in the verification email.
    if (claimedDomain) {
      return ClaimDomainStep.Done;
    }
    // The Error step will be the initial if you were just redirected back to the settings page with an error message.
    if (domainVerificationError) {
      return ClaimDomainStep.Error;
    }
    // By default, we're starting the process to claim a domain.
    return ClaimDomainStep.EnterDomain;
  });
  const [domain, setDomain] = useState<string>('');
  const [emailUser, setEmailUser] = useState<string>('');
  const [ldapUsername, setLdapUsername] = useState<string>('');
  const [ldapPassword, setLdapPassword] = useState<string>('');
  const [samlLoginUrl, setSamlLoginUrl] = useState<string | null>(null);

  useOnMount(() => {
    if (!possibleInitialSteps.includes(step)) {
      // For SAML and LDAP, the first step is to enter the domain to be claimed.
      setStep(ClaimDomainStep.EnterDomain);
    }
  });

  const onCancel = () => {
    onHide();
    setStep(ClaimDomainStep.EnterDomain);
    setDomain('');
    setEmailUser('');
    setSamlLoginUrl(null);
    setIsSubmittingConfiguration(false);
    setSubmissionErrors(null);
  };

  const onSubmitConfiguration = async () => {
    const email = `${emailUser}@${domain}`.trim();
    setIsSubmittingConfiguration(true);
    if (method === OrganizationSsoMethod.SAML) {
      try {
        const response = await claimSAMLDomain({ ...sso, email } as ClaimSamlDomainRequest);
        if (response?.data) {
          setSamlLoginUrl(response.data.redirectUrl);
          setStep(ClaimDomainStep.AuthSaml);
        }
      } catch (error: unknown) {
        setSubmissionErrors(errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.toString()));
      } finally {
        setIsSubmittingConfiguration(false);
      }
    } else {
      try {
        await claimLDAPDomain({
          email,
          username: ldapUsername,
          password: ldapPassword,
        });
        setStep(ClaimDomainStep.CheckEmail);
      } catch (error: unknown) {
        setSubmissionErrors(errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.toString()));
      } finally {
        setIsSubmittingConfiguration(false);
      }
    }
  };

  const onNextStep = () => {
    switch (step) {
      case ClaimDomainStep.EnterDomain:
        // Trim out spaces from the domain if it has any.
        if (/\s/.test(domain)) {
          setDomain(domain.trim());
        }
        setStep(ClaimDomainStep.EnterEmail);
        break;
      case ClaimDomainStep.EnterEmail:
        if (method === OrganizationSsoMethod.SAML) {
          // SAML doesn't require the user to enter more information after this step.
          // No need to await.
          onSubmitConfiguration();
        } else {
          setStep(ClaimDomainStep.AuthLdap);
        }
        break;
      case ClaimDomainStep.AuthSaml:
        setStep(ClaimDomainStep.CheckEmail);
        break;
      case ClaimDomainStep.AuthLdap:
        // LDAP doesn't require the user to enter more information after this step.
        // No need to await.
        onSubmitConfiguration();
        break;
      default:
      // This branch should never be reached.
    }
  };

  let title: string = 'Claim New Domain';
  let stepBody;
  let stepButtons = null;
  if (step === ClaimDomainStep.EnterDomain) {
    stepBody = <EnterDomainStepBody domain={domain} setDomain={setDomain} />;
    stepButtons = <EnterDomainStepButtons domain={domain} onCancel={onCancel} onNextStep={onNextStep} />;
  } else if (step === ClaimDomainStep.EnterEmail) {
    stepBody = (
      <EnterEmailStepBody
        domain={domain}
        emailUser={emailUser}
        setEmailUser={setEmailUser}
        ssoConfigurationStatus={{
          errors: submissionErrors,
          loading: isSubmittingConfiguration,
          isLoading: isSubmittingConfiguration,
        }}
      />
    );
    stepButtons = (
      <EnterEmailStepButtons
        emailUser={emailUser}
        ssoConfigurationStatus={ssoConfigurationStatus}
        onCancel={() => {
          // Clear out the configuration status error in case there was one.
          setSubmissionErrors(null);
          setIsSubmittingConfiguration(false);
          setStep(ClaimDomainStep.EnterDomain);
        }}
        onNextStep={onNextStep}
      />
    );
  } else if (step === ClaimDomainStep.AuthSaml && samlLoginUrl) {
    stepBody = <AuthSamlStepBody domain={domain} />;
    stepButtons = <AuthSamlStepButtons loginUrl={samlLoginUrl} />;
  } else if (step === ClaimDomainStep.AuthLdap && method === OrganizationSsoMethod.LDAP) {
    stepBody = (
      <AuthLdapStepBody
        domain={domain}
        ssoConfigurationStatus={ssoConfigurationStatus}
        username={ldapUsername}
        password={ldapPassword}
        setUsername={setLdapUsername}
        setPassword={setLdapPassword}
      />
    );
    stepButtons = (
      <AuthLdapStepButtons
        isValid={!!ldapUsername && !!ldapPassword}
        ssoConfigurationStatus={ssoConfigurationStatus}
        onCancel={() => {
          setStep(ClaimDomainStep.EnterEmail);
          setLdapUsername('');
          setLdapPassword('');
        }}
        onSignIn={onNextStep}
      />
    );
  } else if (step === ClaimDomainStep.CheckEmail && method !== OrganizationSsoMethod.SAML) {
    stepBody = (<CheckEmailModalBody domain={domain} />);
  } else if (step === ClaimDomainStep.Done && claimedDomain) {
    stepBody = <DoneStepBody domain={claimedDomain} />;
    title = `Domain Claimed — ${claimedDomain}`;
  } else {
    stepBody = (
      <div className="margin-bottom-md">
        <b>{domainVerificationError || 'An error occurred.'}</b>
      </div>
    );
    title = 'Error';
  }

  return (
    <Modal show={isOpen} onHide={onCancel}>
      <Modal.Header closeButton={step >= ClaimDomainStep.CheckEmail}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{stepBody}</Modal.Body>
      {stepButtons && <Modal.Footer>{stepButtons}</Modal.Footer>}
    </Modal>
  );
};

export default MultiStepsClaimDomainModal;
